import { useCallback, useEffect, useRef, useState } from "react";

import Stack from "@mui/material/Stack";
import { AutocompleteGetTagProps } from "@mui/material/useAutocomplete";

import { AutocompleteOption } from "@/dashboard-design-system/Autocomplete/types";
import { DashboardChipShape, DashboardChipVariant } from "@/dashboard-design-system/DashboardChip";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip/DashboardChip";

const MAX_CHIP_WIDTH = 150; // Max width per chip including margin
const OVERFLOW_CHIP_WIDTH = 100; // Estimated width for "X others..." chip
const MARGIN_BUFFER = 16; // Extra margin buffer
const INPUT_MIN_WIDTH = 100; // Minimum width to reserve for the search input

export const useTagRendering = ({
  tags,
  ignoreInputWidth = false,
  hideTags = false,
  chipProps = { shape: "rounded", variant: "filled" },
}: {
  tags: (string | AutocompleteOption)[];
  ignoreInputWidth?: boolean;
  hideTags?: boolean;
  chipProps?: { shape?: DashboardChipShape; variant?: DashboardChipVariant };
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [visibleTagCount, setVisibleTagCount] = useState(tags.length);
  const [availableWidth, setAvailableWidth] = useState(0);

  const calculateVisibleTags = useCallback(
    (width: number, tagCount: number) => {
      if (width === 0 || tagCount === 0) {
        return tagCount;
      }

      // Reserve space for the search input
      const spaceForTags = width - (ignoreInputWidth ? 0 : INPUT_MIN_WIDTH) - MARGIN_BUFFER;

      if (spaceForTags <= 0) {
        return 0; // No space for any tags
      }

      // Check if all tags fit without overflow
      const totalWidthForAllTags = tagCount * MAX_CHIP_WIDTH;
      if (totalWidthForAllTags <= spaceForTags) {
        return tagCount;
      }

      // Calculate how many tags fit with overflow chip
      const widthForOverflow = OVERFLOW_CHIP_WIDTH;
      const availableForTags = spaceForTags - widthForOverflow;
      const maxVisibleTags = Math.floor(availableForTags / MAX_CHIP_WIDTH);

      // If we can't fit even one tag with overflow, just show overflow
      return Math.max(0, maxVisibleTags);
    },
    [ignoreInputWidth]
  );

  const updateVisibleCount = useCallback(() => {
    // Use relative traversal to find measurement target
    const measurementTarget = containerRef.current?.parentElement;

    if (!measurementTarget) {
      return;
    }

    const newAvailableWidth = measurementTarget.offsetWidth;
    if (newAvailableWidth !== availableWidth && newAvailableWidth > 0) {
      setAvailableWidth(newAvailableWidth);
      const newVisibleCount = calculateVisibleTags(newAvailableWidth, tags.length);
      setVisibleTagCount(newVisibleCount);
    }
  }, [availableWidth, calculateVisibleTags, tags.length]);

  // Update when tags change
  useEffect(() => {
    const newVisibleCount = calculateVisibleTags(availableWidth, tags.length);
    setVisibleTagCount(newVisibleCount);
  }, [tags.length, availableWidth, calculateVisibleTags]);

  // Set up resize observer
  useEffect(() => {
    const measurementTarget = containerRef.current?.parentElement;

    if (!measurementTarget) {
      return;
    }

    updateVisibleCount();

    const resizeObserver = new ResizeObserver(() => {
      updateVisibleCount();
    });

    resizeObserver.observe(measurementTarget);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateVisibleCount]);

  const shouldShowOverflow = visibleTagCount < tags.length;
  const overflowCount = tags.length - visibleTagCount;

  const renderTags = useCallback(
    (tagList: (string | AutocompleteOption)[], getTags: AutocompleteGetTagProps) => {
      const visibleTags = shouldShowOverflow ? tagList.slice(0, visibleTagCount) : tagList;

      const renderedTags = visibleTags.map((tag, index) => {
        const tagProps = getTags({ index });
        return (
          <DashboardChip
            variant={chipProps.variant}
            vibe={typeof tag === "string" ? "neutral" : tag.vibe || "brand"}
            shape={chipProps.shape}
            key={typeof tag === "string" ? tag : (tag.value as string)}
            label={typeof tag === "string" ? tag : tag.label}
            data-tag-index={tagProps["data-tag-index"]}
            onDelete={tagProps.onDelete}
            tabIndex={tagProps.tabIndex}
            sx={(theme) => ({
              maxWidth: MAX_CHIP_WIDTH,
              ...theme.mixins.truncateOverflow,
            })}
          />
        );
      });

      if (shouldShowOverflow) {
        renderedTags.push(
          <DashboardChip
            variant={chipProps.variant}
            vibe="brand"
            shape={chipProps.shape}
            key="overflow-chip"
            label={`${overflowCount} others...`}
          />
        );
      }

      return (
        <Stack
          ref={containerRef}
          direction="row"
          gap={1}
          flexWrap="nowrap"
          overflow="hidden"
          width={hideTags ? 0 : "unset"}
        >
          {renderedTags}
        </Stack>
      );
    },
    [shouldShowOverflow, visibleTagCount, hideTags, overflowCount, chipProps.shape, chipProps.variant]
  );

  return {
    renderTags,
  };
};
